import { BlocCoordinator } from "@lib/bloc/BlocCoordinator"
import { compare, SortOrder } from "@lib/Comparable"
import { AssetCategory } from "@model/assets/AssetCategory"
import { AssetClass } from "@model/assets/AssetClass"
import { AssetMake } from "@model/assets/AssetMake"
import { AssetModel } from "@model/assets/AssetModel"
import { AssetType } from "@model/assets/AssetType"
import { Company } from "@model/company/Company"
import { CompanyAlias } from "@model/company/CompanyAlias"
import { DashboardFilter } from "@model/dashboard/DashboardFilter"
import { District } from "@model/district/District"
import { Filter, FilterValue, isFilterEmptyOrContainsValue } from "@model/filters/Filter"
import { FilterPreset, FilterPresetForm, FilterPresetType } from "@model/filters/FilterPresets"
import { Group } from "@model/group/Group"
import { Site } from "@model/site/Site"
import { CompanyBloc, CompanyState } from "@state/company/CompanyBloc"
import { DashboardFilterOverlayBloc, DashboardFilterOverlayState } from "@state/dashboard/DashboardFilterOverlayBloc"
import { DashboardScreenBloc } from "@state/dashboard/DashboardScreenBloc"
import {
    FilterOverlayBlocCoordinator,
    FilterOverlayBlocCoordinatorMixin,
} from "@state/filters/FilterOverlayBlocCoordinatorMixin"
import { StatusBloc } from "@state/StatusBloc"
import { UserBloc } from "@state/user/UserBloc"
import { UserPreferencesBloc, UserPreferencesState } from "@state/UserPreferencesBloc"

type Dependencies = [DashboardFilterOverlayState, UserPreferencesState, CompanyState]
type StateSelection = DashboardFilterOverlayState & {
    parentCompany: Company
    subCompanies: Company[]
    makes: AssetMake[]
    models: AssetModel[]
    types: AssetType[]
    categories: AssetCategory[]
    classes: AssetClass[]
    districts: District[]
    subDistricts: District[]
    units: District[]
    groups: Group[]
    aliases: Record<CompanyAlias, string>
    sites: Site[]
    filterPresets: FilterPreset<DashboardFilter>[]
}

export class DashboardFilterOverlayViewModel
    extends BlocCoordinator<Dependencies, StateSelection>
    implements FilterOverlayBlocCoordinator<DashboardFilter>
{
    private filterOverlayMixin: FilterOverlayBlocCoordinatorMixin<DashboardFilter>

    constructor(
        filterOverlayBloc: DashboardFilterOverlayBloc,
        userPreferencesBloc: UserPreferencesBloc,
        dashboardScreenBloc: DashboardScreenBloc,
        companyBloc: CompanyBloc,
        statusBloc: StatusBloc,
        userBloc: UserBloc
    ) {
        super([filterOverlayBloc, userPreferencesBloc, companyBloc])

        this.filterOverlayMixin = new FilterOverlayBlocCoordinatorMixin(
            FilterPresetType.Dashboard,
            filterOverlayBloc,
            dashboardScreenBloc,
            userPreferencesBloc,
            statusBloc,
            userBloc
        )
    }

    transform = ([filterOverlayState, userPreferencesState, companyState]: Dependencies): StateSelection => {
        const filteredDistricts = companyState.districts.filter((it) =>
            isFilterEmptyOrContainsValue(filterOverlayState.filters[DashboardFilter.SubCompany], [it.companyId])
        )

        const filteredSubDistricts = companyState.subDistricts.filter(
            (it) =>
                isFilterEmptyOrContainsValue(filterOverlayState.filters[DashboardFilter.SubCompany], [it.companyId]) &&
                isFilterEmptyOrContainsValue(filterOverlayState.filters[DashboardFilter.District], [it.parentId ?? 0])
        )

        const filteredUnits = companyState.units.filter(
            (it) =>
                filteredSubDistricts.find((subDistrict) => subDistrict.id === it.parentId) !== undefined &&
                isFilterEmptyOrContainsValue(filterOverlayState.filters[DashboardFilter.SubDistrict], [
                    it.parentId ?? 0,
                ])
        )

        return {
            ...filterOverlayState,
            aliases: companyState.aliases,
            parentCompany: {
                id: companyState.companyId,
                name: companyState.companyName,
                parentId: -1,
                logoUrl: companyState.companyLogoUrl,
            },
            subCompanies: companyState.subCompanies,
            sites: companyState.sites,
            districts: filteredDistricts,
            subDistricts: filteredSubDistricts,
            units: filteredUnits,
            groups: companyState.groups.filter((it) =>
                isFilterEmptyOrContainsValue(filterOverlayState.filters[DashboardFilter.SubCompany], [it.companyId])
            ),
            makes: companyState.assetMakes,
            models: companyState.assetModels.filter((it) =>
                isFilterEmptyOrContainsValue(filterOverlayState.filters[DashboardFilter.Make], [it.makeId])
            ),
            types: companyState.assetTypes,
            categories: companyState.assetCategories.filter((it) =>
                isFilterEmptyOrContainsValue(filterOverlayState.filters[DashboardFilter.AssetType], [it.typeId])
            ),
            classes: companyState.assetClasses.filter((it) =>
                isFilterEmptyOrContainsValue(filterOverlayState.filters[DashboardFilter.Category], [it.categoryId])
            ),
            preset: filterOverlayState.preset,
            filterPresets: userPreferencesState.filterPresets.dashboard.sort((a, b) =>
                compare(a.name, b.name, SortOrder.Asc)
            ),
        }
    }

    toggleOverlayVisibility = () => this.filterOverlayMixin.toggleOverlayVisibility()

    updateFilters = (filters: Record<DashboardFilter, Filter<DashboardFilter>>) =>
        this.filterOverlayMixin.updateFilters(filters)

    updateFilter = (type: DashboardFilter, value: FilterValue[]) => this.filterOverlayMixin.updateFilter(type, value)

    resetFilters = () => this.filterOverlayMixin.resetFilters()

    applyFilters = async () => this.filterOverlayMixin.applyFilters()

    filterPresetSelected = (id: number) => this.filterOverlayMixin.filterPresetSelected(id)

    showCreatePreset = () => this.filterOverlayMixin.showCreatePreset()

    hideCreatePreset = () => this.filterOverlayMixin.hideCreatePreset()

    createPreset = (form: FilterPresetForm) => this.filterOverlayMixin.createPreset(form)

    updatePresetFilters = () => this.filterOverlayMixin.updatePresetFilters()

    updatePresetForm = (preset: FilterPreset<DashboardFilter>, form: FilterPresetForm) =>
        this.filterOverlayMixin.updatePresetForm(preset, form)

    deletePreset = (preset: FilterPreset<DashboardFilter>) => this.filterOverlayMixin.deletePreset(preset)

    showManagePresets = () => this.filterOverlayMixin.showManagePresets()

    hideManagePresets = () => this.filterOverlayMixin.hideManagePresets()

    isSearchFilterValid = () => this.filterOverlayMixin.isSearchFilterValid()

    isSecondaryFilterValid = () => this.filterOverlayMixin.isSecondaryFilterValid()

    isSaving = () => this.filterOverlayMixin.isSaving()

    IsInMaintenance25Revision(): boolean {
        return this.filterOverlayMixin.IsInMaintenance25Revision()
    }
}
